/**
 * Detecção de dispositivo, navegador e versão da plataforma (presença + analytics).
 */

import { PLATFORM_APP_VERSION } from "../appVersion";
import themes from "../context/themes";
import { getPersistedAppThemeKey } from "../context/ThemeContext";

/** @typedef {'iphone'|'ipad'|'android'|'windows'|'macos'|'linux'|'chromebook'|'unknown'} ClientDeviceType */
/** @typedef {'chrome'|'safari'|'firefox'|'edge'|'opera'|'samsung'|'unknown'} ClientBrowserName */

const DEVICE_TYPES = new Set(["iphone", "ipad", "android", "windows", "macos", "linux", "chromebook", "unknown"]);
const BROWSER_NAMES = new Set(["chrome", "safari", "firefox", "edge", "opera", "samsung", "unknown"]);

function normalizeDeviceType(value) {
  const key = String(value || "").trim().toLowerCase();
  return DEVICE_TYPES.has(key) ? key : "unknown";
}

function normalizeBrowserName(value) {
  const key = String(value || "").trim().toLowerCase();
  return BROWSER_NAMES.has(key) ? key : "unknown";
}

/** @param {string} [value] */
export function normalizeAppVersion(value) {
  const s = String(value || "").trim().slice(0, 32);
  if (!s) return null;
  if (!/^\d[\d.]*(-[\w.]+)?$/.test(s)) return null;
  return s;
}

/** @param {string} [value] */
export function normalizeAppTheme(value) {
  const key = String(value || "").trim();
  if (!key || !themes[key]) return null;
  return key;
}

/**
 * @param {string} [userAgent]
 * @returns {{ deviceType: ClientDeviceType, browserName: ClientBrowserName, deviceLabel: string, browserLabel: string }}
 */
export function parseClientEnvironment(userAgent) {
  const ua = String(userAgent || (typeof navigator !== "undefined" ? navigator.userAgent : "") || "");

  let deviceType = "unknown";
  const isIOS = /iPhone|iPad|iPod/i.test(ua);
  const isAndroid = /Android/i.test(ua);

  if (/iPad/i.test(ua) || (isIOS && /Macintosh/i.test(ua) && typeof navigator !== "undefined" && navigator.maxTouchPoints > 1)) {
    deviceType = "ipad";
  } else if (/iPhone|iPod/i.test(ua)) {
    deviceType = "iphone";
  } else if (isAndroid) {
    deviceType = "android";
  } else if (/CrOS/i.test(ua)) {
    deviceType = "chromebook";
  } else if (/Windows/i.test(ua)) {
    deviceType = "windows";
  } else if (/Macintosh|Mac OS X/i.test(ua)) {
    deviceType = "macos";
  } else if (/Linux/i.test(ua)) {
    deviceType = "linux";
  }

  let browserName = "unknown";
  if (/SamsungBrowser/i.test(ua)) {
    browserName = "samsung";
  } else if (/Edg\//i.test(ua) || /Edge\//i.test(ua)) {
    browserName = "edge";
  } else if (/OPR\/|Opera/i.test(ua)) {
    browserName = "opera";
  } else if (/Firefox\//i.test(ua)) {
    browserName = "firefox";
  } else if (/CriOS/i.test(ua) || (/Chrome\//i.test(ua) && !/Edg\//i.test(ua) && !/OPR\//i.test(ua))) {
    browserName = "chrome";
  } else if (/Safari\//i.test(ua) && !/Chrome\//i.test(ua) && !/CriOS/i.test(ua)) {
    browserName = "safari";
  }

  return {
    deviceType,
    browserName,
    deviceLabel: deviceType,
    browserLabel: browserName,
  };
}

/** Snapshot do ambiente atual do navegador. */
export function getCurrentClientEnvironment() {
  const parsed = parseClientEnvironment();
  return {
    deviceType: parsed.deviceType,
    browserName: parsed.browserName,
    appVersion: normalizeAppVersion(PLATFORM_APP_VERSION),
    appTheme: normalizeAppTheme(getPersistedAppThemeKey()),
  };
}

/**
 * @param {(key: string, opts?: object) => string} t
 */
export function getClientEnvironmentDeviceLabel(deviceType, t) {
  const key = normalizeDeviceType(deviceType);
  if (key === "unknown") return t("platformPage.clientEnv.device.unknown");
  return t(`platformPage.clientEnv.device.${key}`);
}

/**
 * @param {(key: string, opts?: object) => string} t
 */
export function getClientEnvironmentBrowserLabel(browserName, t) {
  const key = normalizeBrowserName(browserName);
  if (key === "unknown") return t("platformPage.clientEnv.browser.unknown");
  return t(`platformPage.clientEnv.browser.${key}`);
}

/**
 * @param {(key: string, opts?: object) => string} t
 */
export function getClientEnvironmentAppVersionLabel(appVersion, t) {
  const version = normalizeAppVersion(appVersion);
  if (!version) return t("platformPage.clientEnv.appVersionUnknown");
  return t("platformPage.clientEnv.appVersion", { version });
}

/**
 * @param {(key: string, opts?: object) => string} tThemes — namespace `themes`
 */
export function getClientEnvironmentAppThemeLabel(appTheme, t, tThemes) {
  const key = normalizeAppTheme(appTheme);
  if (!key) return t("platformPage.clientEnv.appThemeUnknown");
  const name = tThemes(key, { defaultValue: key });
  return t("platformPage.clientEnv.appTheme", { theme: name });
}

/**
 * Lista única de dispositivos/navegadores/versões nas abas Xpro 4 de um usuário.
 * @param {{ legacyTabCount?: number, xpro4Tabs?: Array<{ deviceType?: string, browserName?: string, appVersion?: string, appTheme?: string }> }|null|undefined} presenceDetail
 */
export function summarizePresenceClientEnvironments(presenceDetail) {
  const tabs = presenceDetail?.xpro4Tabs || [];
  const deviceTypes = [];
  const browserNames = [];
  const appVersions = [];
  const appThemes = [];
  const seenDevices = new Set();
  const seenBrowsers = new Set();
  const seenVersions = new Set();
  const seenThemes = new Set();

  for (const tab of tabs) {
    const deviceType = normalizeDeviceType(tab?.deviceType);
    const browserName = normalizeBrowserName(tab?.browserName);
    const appVersion = normalizeAppVersion(tab?.appVersion ?? tab?.app_version);
    const appTheme = normalizeAppTheme(tab?.appTheme ?? tab?.app_theme);
    if (deviceType !== "unknown" && !seenDevices.has(deviceType)) {
      seenDevices.add(deviceType);
      deviceTypes.push(deviceType);
    }
    if (browserName !== "unknown" && !seenBrowsers.has(browserName)) {
      seenBrowsers.add(browserName);
      browserNames.push(browserName);
    }
    if (appVersion && !seenVersions.has(appVersion)) {
      seenVersions.add(appVersion);
      appVersions.push(appVersion);
    }
    if (appTheme && !seenThemes.has(appTheme)) {
      seenThemes.add(appTheme);
      appThemes.push(appTheme);
    }
  }

  return { deviceTypes, browserNames, appVersions, appThemes };
}

function tabSessionFromTabKey(tabKey) {
  const s = String(tabKey || "");
  const idx = s.indexOf("::");
  if (idx > 0) return s.slice(0, idx);
  if (s.startsWith("path:")) return null;
  return s || null;
}

/**
 * Mescla telemetria de ambiente vinda da API (Redis) nas abas Xpro 4 do snapshot de presença.
 * @param {{ legacyTabCount?: number, xpro4Tabs?: Array<object> }|null|undefined} detail
 * @param {Array<{ tab_session_id?: string, device_type?: string, browser_name?: string, app_version?: string, app_theme?: string, path?: string, since?: string, last_activity_at?: string, focused?: boolean }>} envItems
 */
export function mergePresenceDetailWithClientEnv(detail, envItems) {
  if (!envItems?.length) return detail;

  const envByTab = new Map(
    envItems
      .filter((row) => row?.tab_session_id)
      .map((row) => [String(row.tab_session_id), row])
  );

  if (!detail) {
    const xpro4Tabs = envItems.map((row) => ({
      path: String(row.path || ""),
      since: row.since != null ? String(row.since) : null,
      lastActivityAt: row.last_activity_at != null ? String(row.last_activity_at) : null,
      focused: Boolean(row.focused),
      tabKey: String(row.tab_session_id),
      deviceType: row.device_type || null,
      browserName: row.browser_name || null,
      appVersion: row.app_version || null,
      appTheme: row.app_theme || null,
    }));
    return { legacyTabCount: 0, xpro4Tabs };
  }

  const existing = detail.xpro4Tabs || [];
  const mergedTabs =
    existing.length > 0
      ? existing.map((tab) => {
          const sid = tabSessionFromTabKey(tab.tabKey);
          const env = sid ? envByTab.get(sid) : null;
          if (!env) return tab;
          return {
            ...tab,
            deviceType: tab.deviceType || env.device_type || null,
            browserName: tab.browserName || env.browser_name || null,
            appVersion: tab.appVersion || env.app_version || null,
            appTheme: tab.appTheme || env.app_theme || null,
          };
        })
      : envItems.map((row) => ({
          path: String(row.path || ""),
          since: row.since != null ? String(row.since) : null,
          lastActivityAt: row.last_activity_at != null ? String(row.last_activity_at) : null,
          focused: Boolean(row.focused),
          tabKey: String(row.tab_session_id),
          deviceType: row.device_type || null,
          browserName: row.browser_name || null,
          appVersion: row.app_version || null,
          appTheme: row.app_theme || null,
        }));

  return {
    ...detail,
    xpro4Tabs: mergedTabs,
  };
}

export { normalizeDeviceType, normalizeBrowserName };
